import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react';
import { useCurrentCaseId } from './useCurrentCase';
import { isFunctionSetter } from 'utils/types';

/**
 * A lightweight utility to reset state whenever the current case changes.
 * Avoids the use of Recoil or other libraries, and retains at most 1 case's value at any moment.
 * If current case is null, the state is locked at `defaultState`.
 * @param defaultState what to return if not set for the current case yet
 * @returns `[value for current case, setter or updater for current case]` (same syntax as `useState`)
 */
export function useCaseScopedState<S>(defaultState: S): [S, Dispatch<SetStateAction<S>>] {
  const currentCaseId = useCurrentCaseId();

  const [state, setState] = useState<{ [caseSmid: string]: S }>({});

  // force reset when going to a different case (but not null)
  // also applies the default state for the first case
  useEffect(() => {
    if (currentCaseId != null && !(currentCaseId in state)) {
      setState({ [currentCaseId]: defaultState });
    }
  }, [state, currentCaseId, defaultState]);

  return useMemo(() => {
    if (currentCaseId == null) {
      return [defaultState, NOOP];
    }

    return [
      state[currentCaseId] ?? defaultState,
      (setterOrUpdater: SetStateAction<S>) =>
        setState((prev) => {
          if (isFunctionSetter(setterOrUpdater)) {
            // when a new setState arrives after a case change, call it on top of the default value
            return { [currentCaseId]: setterOrUpdater(prev[currentCaseId] ?? defaultState) };
          }
          return { [currentCaseId]: setterOrUpdater };
        }),
    ];
  }, [state, defaultState, currentCaseId]);
}

const NOOP = () => {};
